/* eslint-disable no-shadow */
import React, { useContext, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
// components
import {
  Outlet, useLocation, useMatches, useNavigate, useSearchParams,
} from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { Helmet } from 'react-helmet';
import PathBuilderContext from './PathBuilderContext';
import RouterNavigateContext from '../../contexts/RouterNavigate';
import WidgetNavProvider from '../WidgetNav/WidgetNavProvider';
// Redux
import { setLogoutRedirect, setRouterStateFromMatches } from '../../../../../ui-kitchen/src/reducers/resyRouterSlice';
import { initResyLocationsConfigThunk } from '../../../../../ui-kitchen/src/reducers/resyLocationsSlice';
import { useCaptureBookingMetadataOnInit } from './useCaptureBookingMetadataOnInit';
// utils
import Logger from '../../../../../ui-kitchen/src/utils/logger';
import { getLocationPath, getLocationUrl, getSearchParamsAsObject } from '../../../../../ui-kitchen/src/utils/locationUtils';
import { useRouteParams, useUpdateParams } from '../../utils/legacyUtils';
import { getCoords } from '../../../scripts/utils';

const getLastMatchProp = (matches, prop, fallback = null) => {
  if (!matches.length) return fallback;
  const lastMatch = matches.slice(-1)[0];
  return lastMatch.handle?.[prop] || fallback;
};

const getResolvedBackPath = (matches, pathBuilder) => {
  let resolvedBackPath = null;
  const backPathKey = getLastMatchProp(matches, 'backPath');
  /* istanbul ignore else */
  if (backPathKey) {
    resolvedBackPath = pathBuilder.backPath(backPathKey);
  }
  return resolvedBackPath;
};

const getResolvedSearch = (matches, pathBuilder) => {
  let resolvedSearch = {};
  const searchKey = getLastMatchProp(matches, 'search');
  /* istanbul ignore else */
  if (searchKey) {
    resolvedSearch = pathBuilder.getSearch();
  }
  return resolvedSearch;
};

const FallbackComponent = ({ error }) => {
  Logger.error('** ErrorBoundary: ', error);
  return <div />;
};
FallbackComponent.propTypes = { error: PropTypes.string.isRequired };

const $locationReadOnlyShim = {
  path: getLocationPath,
  url: getLocationUrl,
  search: getSearchParamsAsObject,
};

let initResyLocationsConfig = false;

const WidgetRouteOutlet = ({
  Analytics, ipdata, initResyLocationsConfigThunk, logoutRedirect, metadataPageTitle, metadataTitle, pageName, setLogoutRedirect, setRouterStateFromMatches,
}) => {
  const matches = useMatches();
  const pathBuilder = useContext(PathBuilderContext);
  const location = useLocation();
  const navigate = useNavigate();

  const wrapperClass = getLastMatchProp(matches, 'wrapperClass', '');
  const resolvedBackPath = getResolvedBackPath(matches, pathBuilder);
  const resolvedSearch = getResolvedSearch(matches, pathBuilder);

  const $routeParamsShim = useRouteParams();
  /* istanbul ignore next */
  const titleString = metadataPageTitle || metadataTitle;

  // Second part of ipdata useEffect that requires Redux and ReactRouter context
  useEffect(() => {
    /* istanbul ignore else */
    if (ipdata.loaded && !initResyLocationsConfig) {
      initResyLocationsConfig = true;
      const params = {
        $rootScope: {},
        $route: { updateParams: (params) => useUpdateParams(params) },
        $routeParams: $routeParamsShim,
      };
      params.coordinates = getCoords(ipdata);
      initResyLocationsConfigThunk(params);
    }
  }, [ipdata.loaded]);

  useEffect(() => {
    setRouterStateFromMatches({ matches, resolvedBackPath, resolvedSearch });
  }, [matches, resolvedBackPath, resolvedSearch]);

  const $rootScopeMetadataShim = {
    bodyClasses: wrapperClass,
    pageName,
    title: titleString,
  };

  const [searchParams] = useSearchParams();
  // this is the referrer defined in app.js for this call, not document.referrer
  const referrer = searchParams.get('ref') || '';

  useCaptureBookingMetadataOnInit();

  // $routeChangeSuccess-equivalent block from app.js
  useEffect(() => {
    const nextLogoutRedirect = pathBuilder.logoutRedirect(location.pathname, logoutRedirect);
    setLogoutRedirect(nextLogoutRedirect);

    window.parent.postMessage({
      event: 'resyModalScrolltop',
    }, '*');

    // Analytics
    try {
      window.setTimeout(() => {
        Analytics.page($locationReadOnlyShim, referrer, $rootScopeMetadataShim);
      }, 0);
    } catch (e) {
      Logger.error(e);
    }
  }, [location]);

  return (
    <ErrorBoundary FallbackComponent={FallbackComponent}>
      <RouterNavigateContext.Provider value={navigate}>
        <Helmet>
          <title>{titleString}</title>
        </Helmet>
        <WidgetNavProvider Analytics={Analytics} />
        <div className={wrapperClass}><Outlet /></div>
      </RouterNavigateContext.Provider>
    </ErrorBoundary>
  );
};

WidgetRouteOutlet.propTypes = {
  Analytics: PropTypes.shape({
    page: PropTypes.func,
  }).isRequired,
  ipdata: PropTypes.shape({
    loaded: PropTypes.bool,
  }).isRequired,
  initResyLocationsConfigThunk: PropTypes.func.isRequired,
  logoutRedirect: PropTypes.string.isRequired,
  metadataPageTitle: PropTypes.string.isRequired,
  metadataTitle: PropTypes.string.isRequired,
  pageName: PropTypes.string.isRequired,
  setLogoutRedirect: PropTypes.func.isRequired,
  setRouterStateFromMatches: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  const { ipdata, resyRouter } = state;
  const {
    logoutRedirect, metadataPageTitle, metadataTitle, pageName,
  } = resyRouter;

  return {
    ipdata,
    logoutRedirect,
    metadataPageTitle,
    metadataTitle,
    pageName,
  };
};

const mapDispatchToProps = {
  initResyLocationsConfigThunk,
  setLogoutRedirect,
  setRouterStateFromMatches,
};

export { WidgetRouteOutlet as ComponentUnderTest, mapStateToProps };
export default connect(mapStateToProps, mapDispatchToProps)(WidgetRouteOutlet);
